import { apiRequest } from './queryClient';

const SESSION_ID_KEY = 'pa_session_id';
const HEARTBEAT_INTERVAL_MS = 60000;

let heartbeatIntervalId: number | null = null;
let currentPagePath: string | null = null;
let sessionId: string | null = null;

function getOrCreateSessionId(): string {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

export function getSessionId(): string {
  if (!sessionId) {
    sessionId = getOrCreateSessionId();
  }
  return sessionId;
}

export async function logPageView(
  pagePath: string,
  pageTitle: string,
  referrer?: string | null,
  userId?: string | null
): Promise<void> {
  try {
    await apiRequest('POST', '/api/analytics/pageview', {
      sessionId: getSessionId(),
      pagePath,
      pageTitle,
      referrer: referrer || null,
      userId: userId || null,
    });
  } catch (error) {
    console.error('[Analytics] Failed to log page view:', error);
  }
}

export async function startPageSession(
  pagePath: string,
  userId?: string | null
): Promise<void> {
  currentPagePath = pagePath;
  
  try {
    await apiRequest('POST', '/api/analytics/session/start', {
      sessionId: getSessionId(),
      pagePath,
      userId: userId || null,
    });
    
    startHeartbeat(pagePath, userId);
  } catch (error) {
    console.error('[Analytics] Failed to start session:', error);
  }
}

export async function endPageSession(
  pagePath?: string,
  userId?: string | null
): Promise<void> {
  const path = pagePath || currentPagePath;
  if (!path) return;
  
  stopHeartbeat();
  
  try {
    await apiRequest('POST', '/api/analytics/session/end', {
      sessionId: getSessionId(),
      pagePath: path,
      userId: userId || null,
    });
  } catch (error) {
    console.error('[Analytics] Failed to end session:', error);
  }
  
  currentPagePath = null;
}

async function sendHeartbeat(pagePath: string, userId?: string | null): Promise<void> {
  try {
    await apiRequest('POST', '/api/analytics/heartbeat', {
      sessionId: getSessionId(),
      pagePath,
      userId: userId || null,
    });
  } catch (error) {
    console.error('[Analytics] Failed to send heartbeat:', error);
  }
}

function startHeartbeat(pagePath: string, userId?: string | null): void {
  stopHeartbeat();
  
  heartbeatIntervalId = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      sendHeartbeat(pagePath, userId);
    }
  }, HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeat(): void {
  if (heartbeatIntervalId !== null) {
    window.clearInterval(heartbeatIntervalId);
    heartbeatIntervalId = null;
  }
}

let isProcessingVisibilityChange = false;
let lastKnownPagePath: string | null = null;

export function initAnalyticsTracking(): () => void {
  const handleVisibilityChange = async () => {
    if (isProcessingVisibilityChange) return;
    isProcessingVisibilityChange = true;
    
    try {
      if (document.visibilityState === 'hidden') {
        if (currentPagePath) {
          lastKnownPagePath = currentPagePath;
          stopHeartbeat();
          await endPageSession(currentPagePath);
        }
      } else if (document.visibilityState === 'visible') {
        const pathToResume = currentPagePath || lastKnownPagePath;
        if (pathToResume) {
          const pageTitle = document.title;
          const referrer = document.referrer || null;
          await logPageView(pathToResume, pageTitle, referrer);
          await startPageSession(pathToResume);
        }
      }
    } finally {
      isProcessingVisibilityChange = false;
    }
  };

  const handleBeforeUnload = () => {
    if (currentPagePath) {
      stopHeartbeat();
      const blob = new Blob(
        [JSON.stringify({
          sessionId: getSessionId(),
          pagePath: currentPagePath,
        })],
        { type: 'application/json' }
      );
      navigator.sendBeacon('/api/analytics/session/end', blob);
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    stopHeartbeat();
  };
}
